import {
  BANK_ACCOUNT_STATUS,
  BANK_CONNECTION_TYPE,
} from "constants/dataSource";
import dayjs from "dayjs";
import { LedgerBanks } from "store/apis/bankConnections";
import { BankConnection, Banks, ErrorItem } from "types/Models/banks";

const CONNECTION_TYPE_PRIORITY = [
  BANK_CONNECTION_TYPE.DIRECT,
  BANK_CONNECTION_TYPE.PLAID,
  BANK_CONNECTION_TYPE.MANUAL,
] as const;

const getBestConnectionAccount = (banks: Banks[]): Banks =>
  CONNECTION_TYPE_PRIORITY.reduce<Banks | undefined>(
    (found, type) =>
      found ?? banks.find((bank) => bank.account.connection_type === type),
    undefined
  ) ?? banks[0];

const isBankGroupConnected = (items: ErrorItem[]) => {
  const trackedItems = items.filter((item) => !item.is_manually_added);
  if (trackedItems.length === 0) return true;
  return trackedItems.every((item) => item.is_bank_connected);
};

export const useDataSourceSegregate = (plaidConnections: LedgerBanks) => {
  const bankAccounts: Record<string, Banks[]> | undefined =
    plaidConnections?.accounts.reduce((acc, obj) => {
      const name = obj.bank_brand.name;
      if (!acc[name]) {
        acc[name] = [];
      }
      acc[name].push(obj);
      return acc;
    }, {} as Record<string, Banks[]>);

  const someActiveSomeClosed = (name: string) => {
    const items = plaidConnections?.items.filter(
      (i) => i.bank_brand.name === name
    );

    return (
      items.some((e) => e.status === BANK_ACCOUNT_STATUS.CLOSED) &&
      items.some((e) => e.status === BANK_ACCOUNT_STATUS.ACTIVE)
    );
  };

  const bankItems: Record<string, ErrorItem[]> | undefined =
    plaidConnections?.items.reduce((acc, obj) => {
      const name = obj.bank_brand.name;

      if (!acc[name]) {
        acc[name] = [];
      }

      if (someActiveSomeClosed(name)) {
        if (obj.status === BANK_ACCOUNT_STATUS.ACTIVE) {
          acc[name].push(obj);
        }
      } else {
        acc[name].push(obj);
      }

      return acc;
    }, {} as Record<string, ErrorItem[]>);

  const allBankConnections: BankConnection[] =
    bankAccounts && bankItems
      ? Object.entries(bankAccounts).map(([bank, banks]) => {
          const bestConnectionAccount = getBestConnectionAccount(banks);

          return {
            connection_provider: banks[0].bank_brand.connection_provider,
            accountant_access: bankItems[bank][0].accountant_access,
            accountant_access_reason:
              bankItems[bank][0].accountant_access_reason,
            direct_connection: banks[0].direct_connection,
            plaid_institution_id: banks[0].bank_brand.plaid_institution_id,
            uuid: bestConnectionAccount.entity_item_id,
            name: bank,
            direct_base_connection_id: bankItems[bank][0].bank_brand.uuid,
            description: "",
            entity_item_id: bestConnectionAccount.entity_item_id,
            logo_url: banks[0].bank_brand.logo,
            last_successful_transaction_update: banks.reduce((latest, bank) => {
              if (!latest) return bank.last_successful_transaction_update;
              if (!bank.last_successful_transaction_update) return latest;
              return dayjs(bank.last_successful_transaction_update).isAfter(
                dayjs(latest)
              )
                ? bank.last_successful_transaction_update
                : latest;
            }, banks[0].last_successful_transaction_update),
            connection_type: bestConnectionAccount.account.connection_type,
            is_connection_available: isBankGroupConnected(bankItems[bank]),
            last_disconnected_at: bankItems[bank][0].last_disconnected_at,
            update_available: bankItems[bank].some((a) => a.update_required),
            is_statement_supported: bankItems[bank][0].is_statement_supported,
            bank_accounts: banks
              .map((bank) => ({
                ...bank.account,
                mark_as_manually_added: bank.mark_as_manually_added,
                is_new_bank_account: bank.is_new_bank_account,
              }))
              .map((account) => ({
                account_number: account.mask ? `•••• ${account.mask}` : "",
                name: account.nickname,
                available_balance: account.current_balance,
                uuid: account.uuid,
                last_reconcilation_status: account.last_reconcilation_status,
                opening_balance: account.opening_balance,
                opening_balance_date: account.opening_balance_date,
                balance_matches: account.balance_matches,
                status: account.status,
                closed_on: account.closed_on,
                closing_balance: account.closing_balance,
                mark_as_manually_added: account.mark_as_manually_added,
                is_new_bank_account: account.is_new_bank_account,
                currency: account.currency,
                is_txn_fetched: account.is_txn_fetched,
              })),
          };
        })
      : [];

  const stripeConnectedBank = allBankConnections.find(
    ({ connection_provider, bank_accounts }) =>
      connection_provider === "STRIPE" &&
      bank_accounts &&
      bank_accounts?.length > 0
  );

  const bankConnections = allBankConnections.filter(
    ({ name, connection_provider, bank_accounts }) =>
      connection_provider !== "STRIPE" &&
      bank_accounts &&
      bank_accounts?.length > 0
  );

  const { accounts: totalAccounts = 0, current_balance: totalBalance = 0 } =
    plaidConnections?.aggregate || {};

  return {
    totalAccounts,
    totalBalance,
    stripeConnectedBank,
    bankConnections,
    allBankConnections,
  };
};
